import React, { useCallback, useRef, FC } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { Button } from '@cubeyou-team/hx-react-components';

import {
  SingleItemLoader,
  TaxonomyExplorerItemLoader,
} from '../ContentLoaders';
import ResultsEmptyStateContainer from '../ResultsEmptyStateContainer';
import ResultSingleItem from './components/ResultSingleItem';
import { ResultsListProps } from './types';

const ResultsList: FC<ResultsListProps> = ({
  items,
  onItemDragToggle,
  onItemSelected,
  shouldCallLoadMore,
  isInitialLoading,
  isNextPageLoading,
  onLoadMoreRequest,
  showModal,
  selectedOptionId,
  currentQ,
  disableModals,
  emptyItemResultMessage,
  noConversationsFoundMessage,
  emptyConversationResultsListTitle,
  emptyConversationResultsListSubtitle,
  selectedDataSource,
  disabledItemSources,
}) => {
  const scrollParentRef = useRef<any | null>(null);
  const loadMoreItems = useCallback(() => {
    if (isNextPageLoading || isInitialLoading) {
      return false;
    }
    onLoadMoreRequest && onLoadMoreRequest();
  }, [isNextPageLoading, isInitialLoading, onLoadMoreRequest]);
  const isItemLoaded = (index) => !shouldCallLoadMore() || index < items.length;

  const handleOnMissingItemRequest = () => {
    showModal(
      {
        isOpen: true,
        position: 'center',
      },
      'RequireMissingItem'
    );
  };

  if (isInitialLoading) {
    return <TaxonomyExplorerItemLoader />;
  }

  if (items.length === 0 && selectedOptionId !== 'conversation') {
    return (
      <ResultsEmptyStateContainer
        title={emptyItemResultMessage}
        icon="search"
        button={
          !disableModals && (
            <Button
              size="small"
              type="button"
              onClick={handleOnMissingItemRequest}
            >
              Request it now!
            </Button>
          )
        }
      />
    );
  }

  if (items.length === 0 && selectedOptionId === 'conversation') {
    if (currentQ && currentQ !== '') {
      return (
        <ResultsEmptyStateContainer
          title={noConversationsFoundMessage}
          icon="search"
        />
      );
    }
    // todo: Overwrite image url
    return (
      <ResultsEmptyStateContainer
        title={emptyConversationResultsListTitle}
        subtitle={emptyConversationResultsListSubtitle}
        image="https://cdn.helixa.ai/images/workspace/conversations.png"
      />
    );
  }

  const listItem = ({ item, index }) => {
    if (!isItemLoaded(index)) {
      return (
        <div key={`loader-${item.id}-${index}`}>
          <SingleItemLoader />
        </div>
      );
    }
    const notConversation = selectedOptionId !== 'conversation';
    const isExternal =
      item.source?.toLowerCase() !== 'twitter' &&
      item.source?.toLowerCase() !== 'discover';
    const isDisabled = Boolean(
      disabledItemSources?.includes(item.source) ||
        disabledItemSources?.includes(item.source?.toLowerCase())
    );
    return (
      <ResultSingleItem
        tabIndex={index + 1}
        key={item.id}
        item={item}
        infoBox={!isExternal && notConversation}
        isDraggable
        disabled={isDisabled}
        onItemSelected={onItemSelected}
        onItemDragToggle={onItemDragToggle}
        showModal={showModal}
        disableModals={disableModals}
        selectedDataSource={selectedDataSource}
      />
    );
  };

  return (
    <div
      style={{ height: '100%', overflow: 'auto', paddingBottom: '60px' }}
      ref={(ref) => {
        scrollParentRef.current = ref;
      }}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMoreItems}
        hasMore={shouldCallLoadMore()}
        threshold={5}
        loader={
          <div key="loader">
            <SingleItemLoader />
          </div>
        }
        useWindow={false}
        getScrollParent={() => scrollParentRef.current}
      >
        {items.map((item, index) => (
          <div key={`list-item-${item.id}-${index}`}>
            {listItem({ item, index })}
          </div>
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default ResultsList;
